import { motion } from "framer-motion";
import temple from "../../accests/kan/kan (1).jpeg";
import peradeniya from "../../accests/kan/kan (7).jpeg";
import scenic from "../../accests/kan/kan (9).jpeg";
import royal from "../../accests/kan/kan (4).jpeg";
import lake from "../../accests/kan/kan (6).jpeg";
import cultural from "../../accests/kan/kan (8).jpeg";
import kandy1 from "../../accests/kan/kan (5).jpeg";
import kandy2 from "../../accests/kan/kan (3).jpeg";
import kandy3 from "../../accests/kan/kan (2).jpeg";

export default function KandyDayTour() {

    const highlights = [
        {
            title: "Temple of the Tooth",
            desc: "Visit the sacred Temple of the Tooth, one of Sri Lanka’s most revered Buddhist sites, and learn about its history and rituals.",
            img: temple
        },
        {
            title: "Royal Botanical Gardens",
            desc: "Explore the lush Royal Botanical Gardens in Peradeniya, home to exotic plants, orchids, and serene walking paths.",
            img: peradeniya
        },
        {
            title: "Scenic Tea Plantations",
            desc: "Drive through rolling hills covered with vibrant tea plantations, enjoying panoramic views and photo stops.",
            img: scenic
        },
        {
            title: "Royal Palace & Cultural Shows",
            desc: "Discover Kandy’s royal heritage and enjoy traditional Kandyan cultural performances.",
            img: royal
        },
        {
            title: "Kandy Lake Walk",
            desc: "Take a peaceful walk around Kandy Lake surrounded by lush greenery and historic landmarks.",
            img: lake
        },
        {
            title: "Cultural Village Visit",
            desc: "Experience authentic Kandyan traditions, crafts, and village life with local communities.",
            img: cultural
        }
    ];

    const itinerary = [
        "Pickup from your hotel",
        "Visit the Temple of the Tooth Relic",
        "Explore Royal Botanical Gardens, Peradeniya",
        "Scenic drive through tea plantations",
        "Visit Royal Palace & cultural show",
        "Relaxing walk around Kandy Lake",
        "Visit a traditional cultural village",
        "Lunch at a local restaurant",
        "Return to your hotel"
    ];

    return (
        <div className="pt-28 bg-gray-50">

            {/* HERO */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="relative h-[60vh]"
            >
                <img
                    src={temple}
                    alt="Kandy Day Tour"
                    className="w-full h-full object-cover brightness-75"
                />

                <div className="absolute inset-0 flex flex-col justify-center items-center text-center text-white px-6">
                    <motion.h1
                        initial={{ y: -50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="text-5xl md:text-6xl font-bold drop-shadow-lg"
                    >
                        Kandy Day Tour
                    </motion.h1>

                    <motion.p
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="mt-4 max-w-2xl text-lg drop-shadow-md"
                    >
                        Discover Sri Lanka’s cultural capital with sacred temples,
                        lush gardens, and scenic hill country views.
                    </motion.p>
                </div>
            </motion.div>

            {/* HIGHLIGHTS */}
            <section data-aos="fade-up"
                     data-aos-duration="3000" className="max-w-7xl mx-auto px-6 py-16">
                <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
                    Tour Highlights
                </h2>

                <div className="grid md:grid-cols-3 gap-8">
                    {highlights.map((item, index) => (
                        <motion.div
                            key={index}
                            whileHover={{ scale: 1.05 }}
                            className="bg-white rounded-3xl shadow-lg overflow-hidden"
                        >
                            <img src={item.img} alt={item.title} className="w-full h-48 object-cover" />
                            <div className="p-6">
                                <h3 className="text-xl font-semibold text-yellow-500 mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-gray-600 text-sm">{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ITINERARY */}
            <section data-aos="fade-up"
                     data-aos-duration="3000" className="bg-gray-100 py-16">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">

                    {/* LEFT */}
                    <div>
                        <h2 className="text-3xl font-bold mb-10 text-gray-800">
                            Detailed Itinerary
                        </h2>

                        <ul className="space-y-4 text-gray-700 list-decimal list-inside">
                            {itinerary.map((step, index) => (
                                <motion.li
                                    key={index}
                                    initial={{ x: -40, opacity: 0 }}
                                    whileInView={{ x: 0, opacity: 1 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="text-lg"
                                >
                                    {step}
                                </motion.li>
                            ))}
                        </ul>
                    </div>

                    {/* RIGHT IMAGES */}
                    <div className="grid grid-cols-2 gap-4">
                        {[kandy1, kandy2, kandy3, lake].map((img, i) => (
                            <motion.img
                                key={i}
                                src={img}
                                whileHover={{ scale: 1.05 }}
                                className="h-40 w-full object-cover rounded-2xl shadow-lg"
                                alt="Kandy tour spot"
                            />
                        ))}
                    </div>

                </div>
            </section>

            {/* WHY CHOOSE + CTA */}
            <section  data-aos="fade-up"
                     data-aos-duration="3000" className="relative py-24 overflow-hidden">

                {/* BACKGROUND */}
                <div className="absolute inset-0">
                    <img
                        src={scenic}
                        alt="Why choose Kandy tour"
                        className="w-full h-full object-cover scale-110 blur-md"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>

                {/* CONTENT */}
                <div data-aos="fade-up"
                     data-aos-duration="3000" className="relative max-w-6xl mx-auto px-6 text-white text-center">

                    <h2 className="text-3xl md:text-4xl font-bold mb-10">
                        Why Choose This Tour?
                    </h2>

                    <ul className="max-w-3xl mx-auto space-y-4 text-lg list-disc list-inside
                                   backdrop-blur-md bg-white/10 p-8 rounded-3xl shadow-xl">
                        <li>Explore Sri Lanka’s cultural capital and sacred temples.</li>
                        <li>Enjoy breathtaking hill country and tea plantation views.</li>
                        <li>Experience traditional Kandyan dance performances.</li>
                        <li>Relax in peaceful surroundings around Kandy Lake.</li>
                        <li>Travel with a friendly and knowledgeable local guide.</li>
                    </ul>

                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mt-14"
                    >
                        <motion.a
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                            href="https://wa.me/94770163670?text=Hello%20I%20would%20like%20to%20book%20the%20Kandy%20Day%20Tour"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-14 py-4
        bg-yellow-500 text-black font-semibold text-lg
        rounded-full shadow-xl hover:bg-yellow-400
        transition-all duration-300"
                        >
                            Book Kandy Day Tour
                        </motion.a>

                        <p className="mt-4 text-sm text-gray-200">
                            🕒 Flexible timing • 🚗 Comfortable transport • 📍 Expert local guide
                        </p>
                    </motion.div>


                </div>
            </section>

        </div>
    );
}
